import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../styles/colors';
import { typography } from '../../styles/typography';
import { spacing } from '../../styles/spacing';

// Input variants
const inputVariants = {
  default: css`
    background: ${colors.background.primary};
    border: 1px solid ${colors.border.interactive};
    color: ${colors.text.primary};
    
    &:focus {
      border-color: ${colors.primary.DEFAULT};
      box-shadow: 0 0 0 3px ${colors.primary.light}40;
      outline: none;
    }
    
    &:hover:not(:focus) {
      border-color: ${colors.border.secondary};
    }
    
    &::placeholder {
      color: ${colors.text.muted};
    }
    
    &:disabled {
      background: ${colors.background.tertiary};
      color: ${colors.text.muted};
      cursor: not-allowed;
    }
  `,
  filled: css`
    background: ${colors.background.secondary};
    border: 1px solid ${colors.background.secondary};
    color: ${colors.text.primary};
    
    &:focus {
      background: ${colors.background.primary};
      border-color: ${colors.primary.DEFAULT};
      box-shadow: 0 0 0 3px ${colors.primary.light}40;
      outline: none;
    }
    
    &:hover:not(:focus) {
      background: ${colors.background.tertiary};
    }
    
    &::placeholder {
      color: ${colors.text.muted};
    }
    
    &:disabled {
      background: ${colors.background.tertiary};
      color: ${colors.text.muted};
      cursor: not-allowed;
    }
  `,
  outline: css`
    background: transparent;
    border: 1px solid ${colors.border.interactive};
    color: ${colors.text.primary};
    
    &:focus {
      border-color: ${colors.primary.DEFAULT};
      box-shadow: 0 0 0 3px ${colors.primary.light}40;
      outline: none;
    }
    
    &:hover:not(:focus) {
      border-color: ${colors.border.secondary};
    }
    
    &::placeholder {
      color: ${colors.text.muted};
    }
    
    &:disabled {
      background: ${colors.background.tertiary};
      color: ${colors.text.muted};
      cursor: not-allowed;
    }
  `,
};

// Input sizes
const inputSizes = {
  sm: css`
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typography.sizes.sm};
    border-radius: ${spacing.radius.lg};
    height: 36px;
  `,
  md: css`
    padding: ${spacing[3]} ${spacing[4]};
    font-size: ${typography.sizes.base};
    border-radius: ${spacing.radius.xl};
    height: 44px;
  `,
  lg: css`
    padding: ${spacing[4]} ${spacing[5]};
    font-size: ${typography.sizes.lg};
    border-radius: ${spacing.radius['2xl']};
    height: 52px;
  `,
};

// Input wrapper for icons
const InputWrapper = styled.div<{ $size: keyof typeof inputSizes }>`
  position: relative;
  display: flex;
  align-items: center;
  
  ${props => {
    switch (props.$size) {
      case 'sm': return css`height: 36px;`;
      case 'md': return css`height: 44px;`;
      case 'lg': return css`height: 52px;`;
      default: return css`height: 44px;`;
    }
  }}
`;

// Styled Input component
const StyledInput = styled.input<{
  $variant: keyof typeof inputVariants;
  $size: keyof typeof inputSizes;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $hasError?: boolean;
}>`
  width: 100%;
  font-family: ${typography.fonts.primary};
  font-weight: ${typography.weights.normal};
  line-height: ${typography.lineHeights.normal};
  transition: all 0.2s ease-in-out;
  
  ${props => inputVariants[props.$variant]}
  ${props => inputSizes[props.$size]}
  
  ${props => props.$hasLeftIcon && css`
    padding-left: ${spacing[10]};
  `}
  
  ${props => props.$hasRightIcon && css`
    padding-right: ${spacing[10]};
  `}
  
  ${props => props.$hasError && css`
    border-color: ${colors.status.error};
    
    &:focus {
      border-color: ${colors.status.error};
      box-shadow: 0 0 0 3px ${colors.status.error}40;
    }
  `}
`;

// Icon wrapper
const IconWrapper = styled.div<{ $position: 'left' | 'right'; $size: keyof typeof inputSizes }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.muted};
  pointer-events: none;
  
  ${props => {
    const position = props.$position === 'left' ? 'left' : 'right';
    const padding = props.$size === 'sm' ? spacing[3] : spacing[4];
    return css`
      ${position}: ${padding};
    `;
  }}
  
  ${props => {
    switch (props.$size) {
      case 'sm': return css`width: 16px; height: 16px;`;
      case 'md': return css`width: 20px; height: 20px;`;
      case 'lg': return css`width: 24px; height: 24px;`;
      default: return css`width: 20px; height: 20px;`;
    }
  }}
`;

// Label component
const Label = styled.label`
  display: block;
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.sm};
  font-weight: ${typography.weights.medium};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

// Error message component
const ErrorMessage = styled.span`
  display: block;
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.sm};
  color: ${colors.status.error};
  margin-top: ${spacing[1]};
`;

// Input props interface
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: keyof typeof inputVariants;
  size?: keyof typeof inputSizes;
  label?: string;
  error?: string;
  hasError?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

// Main Input component
const Input = forwardRef<HTMLInputElement, InputProps>(({
  variant = 'default',
  size = 'md',
  label,
  error,
  hasError,
  leftIcon,
  rightIcon,
  fullWidth = true,
  className,
  ...props
}, ref) => {
  const inputId = React.useId();
  const errorId = React.useId();

  return (
    <div className={className} style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <InputWrapper $size={size}>
        {leftIcon && (
          <IconWrapper $position="left" $size={size}>
            {leftIcon}
          </IconWrapper>
        )}
        <StyledInput
          ref={ref}
          id={inputId}
          $variant={variant}
          $size={size}
          $hasLeftIcon={!!leftIcon}
          $hasRightIcon={!!rightIcon}
          $hasError={hasError}
          aria-invalid={!!error || hasError}
          aria-describedby={error ? errorId : undefined}
          {...props}
        />
        {rightIcon && (
          <IconWrapper $position="right" $size={size}>
            {rightIcon}
          </IconWrapper>
        )}
      </InputWrapper>
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
